import productService from '.';
import articles from './articles';
import constants from '../../settings/constants';
import { ITEMS, RANGES, UNIT_CONVERSIONS, UNITS } from '../../constants';
import { metricToImperial } from '../../util/measures';

const products = [];

/**
 * Gets the sprite item type for a specific product
 * @param {*} product
 * @returns {String}
 */
function calcType(product) {
  if (product.id.indexOf(ITEMS.SECTION) === 0) {
    return ITEMS.SECTION;
  }
  if (
    [
      ITEMS.SHELF,
      ITEMS.METAL_SHELF,
      ITEMS.WIRE_SHELF,
      ITEMS.FELT_SHELF,
      ITEMS.ADD_ON,
      ITEMS.DIVIDER,
      ITEMS.FRAME,
      ITEMS.SHELVING_UNIT,
      ITEMS.LEG,
      ITEMS.CLOTHES_RAIL,
      ITEMS.COVER,
      ITEMS.TOP_SHELF,
      ITEMS.UPRIGHT,
      ITEMS.DRYING_RACK,
      ITEMS.SHOE_SHELF,
      ITEMS.TROUSER_HANGER,
      ITEMS.MOUNTING_RAIL,
      ITEMS.SIDEWALL,
      ITEMS.DUMMY,
      ITEMS.TABLE,
      ITEMS.BOTTLE_RACK,
      ITEMS.SHELF_DRAWER,
      ITEMS.DRAWER,
      ITEMS.DOOR,
      ITEMS.SHELF_CLOTHES_RAIL,
    ].some(type => type === product.filter.type)
  ) {
    return product.filter.type;
  }
  return ITEMS.ITEM;
}

function addModelData(products, models) {
  function roundBounds(product) {
    if (models[product.modelid]) {
      const bounds = models[product.modelid].bounds.default.size;
      return Object.entries(bounds).reduce((out, [dim, val]) => {
        out[dim] = Math.round(val);
        return out;
      }, {});
    }
    return {
      depth: 0,
      height: 0,
      width: 0,
    };
  }

  function getLogic(product) {
    if (models[product.modelid]) {
      return models[product.modelid].logic;
    }
    return {};
  }
  return products.map(function (prod) {
    return {
      ...prod,
      bounds: roundBounds(prod),
      logic: getLogic(prod),
    };
  });
}

export function setProducts(loadedFiles) {
  const productData = loadedFiles.find(chunk =>
    chunk.hasOwnProperty('products')
  );
  const modelData = loadedFiles.find(chunk => chunk.hasOwnProperty('models'));

  const modelEnrichedProducts = addModelData(
    productData.products,
    modelData.models
  );

  products.length = 0;
  products.push(
    ...modelEnrichedProducts.map(function (prod) {
      return {
        depth: prod.bounds.depth,
        height: prod.bounds.height,
        width: prod.bounds.width,
        id: prod.id,
        modelid: prod.modelid,
        type: calcType(prod),
        iows: prod.iows,
        name: prod.itemname,
        logic: prod.logic,
        filter: prod.filter,
        parts: prod.parts,
        sprno: prod.sprno,
      };
    })
  );
  productService.init();
}

function validateSections() {
  // ditch any sections that don't have matching shelves
  productService.getSections().forEach(function hasShelves(section) {
    const valid = productService
      .getSectionInserts()
      .some(function sameFootprint(shelf) {
        const offset = productService.getSectionOffset(shelf);
        return (
          shelf.filter.depth + offset.x * 2 === section.filter.depth &&
          shelf.filter.width + offset.z * 2 === section.filter.width
        );
      });
    section.valid = valid;
    if (!valid) {
      console.log('invalidated section', section, 'due to missing shelves');
    }
  });
}

/**
 * Returns measurement according to (S)ystem (o)f (M)easurement
 *
 * @param useMetricMeasures {boolean}
 * @param val {number}
 * @param unit {string}
 * @returns {number|string}
 */
export const getMeasurementBySoM = (useMetricMeasures, val, unit = UNITS.cm) =>
  useMetricMeasures
    ? Math.ceil(val / UNIT_CONVERSIONS[unit])
    : metricToImperial(val).onlyInches;

export function validateProducts() {
  // first flag anything missing parts as invalid
  products.forEach(function validate(product) {
    product.valid =
      product.iows
        .map(iow => iow.itemno)
        .every(itemno => articles.getArticleContent(itemno)?.ruItemNo) &&
      (!product.sprno || articles.getArticleContent(product.sprno)?.ruItemNo);
  });

  function requireAllSectionPartsToBeValid() {
    return productService.getSections().forEach(section => {
      section.valid = Object.values(section.parts).every(
        part => productService.getProduct(part)?.valid
      );
    });
  }

  function requireAllShelfDrawerPartsToBeValid() {
    const isShelfDrawer = item =>
      productService.isType(item, ITEMS.SHELF_DRAWER);
    const getParts = item => Object.values(item.parts);
    return productService
      .getAll()
      .filter(isShelfDrawer)
      .forEach(shelfDrawer => {
        shelfDrawer.valid = getParts(shelfDrawer).every(
          part => productService.getProduct(part)?.valid
        );
      });
  }

  switch (constants.APPLICATION_NAME) {
    case 'BROR':
      validateSections();

      // remove cabinet if nowhere to fit
      const cabinets = productService
        .getAll()
        .filter(productService.isCabinet)
        .filter(productService.isInsert);

      cabinets.forEach(function (cabinet) {
        cabinet.valid = productService
          .getSections()
          .some(function fitsCabinet(section) {
            const offset = productService.getSectionOffset(cabinet);

            return (
              cabinet.filter.depth + offset.z * 2 === section.filter.depth &&
              cabinet.filter.width + offset.x * 2 === section.filter.width
            );
          });
      });
      break;
    case RANGES.BOAXEL:
      validateSections();
      break;
    case RANGES.AURDAL:
      //Require all parts for a section to be valid
      requireAllSectionPartsToBeValid();
      break;
    case RANGES.IVAR:
      const regularAndKnockdownSideUnitPairs = [
        {
          regular: 'side_unit_5x30x124_back_main', // 73755709
          knockdown: 'side_unit_5x30x124_back_main_knock_down', // 90484649
        },
        {
          regular: 'side_unit_5x50x124_back_main', // 53755809
          knockdown: 'side_unit_5x50x124_back_main_knock_down', // 10484653
        },
        {
          regular: 'side_unit_5x30x179_back_main', // 83756609
          knockdown: 'side_unit_5x30x179_back_main_knock_down', // 50484651
        },
        {
          regular: 'side_unit_5x50x179_back_main', // 63756709
          knockdown: 'side_unit_5x50x179_back_main_knock_down', // 90484654
        },
        {
          regular: 'side_unit_5x30x226_back_main', // 57489509
          knockdown: 'side_unit_5x30x226_back_main_knock_down', // 30484652
        },
        {
          regular: 'side_unit_5x50x226_back_main', // 87489409
          knockdown: 'side_unit_5x50x226_back_main_knock_down', // 60484655
        },
      ];
      regularAndKnockdownSideUnitPairs.forEach(({ regular, knockdown }) => {
        const regularProduct = productService.getProduct(regular);
        const knockdownProduct = productService.getProduct(knockdown);

        // If both variants are available, we use the knockdown one.
        if (regularProduct) regularProduct.valid = !knockdownProduct?.valid;
      });

      productService.getSections().forEach(section => {
        // Filter out only the parts available on the market
        section.parts = Object.fromEntries(
          Object.entries(section.parts).filter(
            ([key, value]) => productService.getProduct(value)?.valid
          )
        );

        // An IVAR section needs two side-panels and one cross-brace to be valid
        section.valid =
          Object.values(section.parts).filter(part => {
            const product = productService.getProduct(part);
            return (
              product?.iows.length &&
              productService.isType(product, [
                ITEMS.SIDE_PANEL,
                ITEMS.CROSS_BRACE,
              ])
            );
          }).length === 3;
      });
      break;
    case RANGES.ELVARLI:
      requireAllSectionPartsToBeValid();
      requireAllShelfDrawerPartsToBeValid();
      break;
    default:
      break;
  }
}

export function flagSuperfluousProductsAsInvalid() {
  switch (constants.APPLICATION_NAME) {
    case RANGES.BOAXEL:
      const mountingRails = productService.getFilteredItems(item =>
        productService.isType(item, ITEMS.MOUNTING_RAIL)
      );
      const mountingRailColors =
        productService.getColorsOfProducts(mountingRails);

      mountingRailColors.forEach(mountingRailColor => {
        if (
          !productService.areMountingRailsOfSpecificColorValid(
            mountingRailColor
          )
        )
          mountingRails.forEach(mountingRail => {
            if (mountingRail.filter.color === mountingRailColor)
              mountingRail.valid = false;
          });
      });

      const oakShelf60Old = productService.getProduct('20448754');
      const oakShelf80Old = productService.getProduct('50448757');
      const oakShelf60New = productService.getProduct('80576079');
      const oakShelf80New = productService.getProduct('60576080');

      const invalidateOldOakShelves = () => {
        oakShelf60Old && (oakShelf60Old.valid = false);
        oakShelf80Old && (oakShelf80Old.valid = false);
      };

      const invalidateNewOakShelves = () => {
        oakShelf60New && (oakShelf60New.valid = false);
        oakShelf80New && (oakShelf80New.valid = false);
      };

      if (oakShelf60New && oakShelf80New) {
        invalidateOldOakShelves();
      } else if (oakShelf60Old && oakShelf80Old) {
        invalidateNewOakShelves();
      } else if (oakShelf60New || oakShelf80New) {
        /* Even if the market has a "complete" set of oak colored shelves consisting
           of an old shelf of one size and a new shelf of the other size, mixing them
           might not be a good idea, since they look the same in the planner, but
           seem to differ slightly in color in reality. */
        invalidateOldOakShelves();
      }

      break;
    case RANGES.AURDAL:
      // If one of our mandatory products are missing for one of the colors,
      // mark all products of that color as invalid
      ['white', 'dark_grey'].forEach(color => {
        if (!productService.hasAllMandatoryProductsOfColor(color)) {
          const productsOfColor = productService.getFilteredItems(Boolean, {
            color,
          });
          productsOfColor.forEach(product => {
            product.valid = false;
          });
        }
      });
      break;
    case RANGES.ELVARLI:
      const getSectionsPostsOfDepth = depth =>
        productService
          .getSections()
          .filter(
            section =>
              productService.isType(section, ITEMS.SECTION_POSTS) &&
              section.depth === depth
          );
      const filterOnPresentAndValid = productIds => {
        const allProducts = productService.getAll();
        const presentAndValidIds = productIds.filter(id =>
          allProducts.map(product => product.id).includes(id)
        );
        return presentAndValidIds;
      };
      const invalidateProducts = products =>
        products.forEach(product => (product.valid = false));

      const insertsRequiring360 = [
        '60313282', // Shelf, white, 40x36
        '20313284', // Shelf, white, 80x36
        '90319268', // Shelf, bamboo, 40x36
        '70319269', // Shelf, bamboo, 80x36
        'shelf_drawer_40_36_white',
        'shelf_drawer_80_36_white',
        'shelf_drawer_40_36_bamboo',
        'shelf_drawer_80_36_bamboo',
        '60313282_shelf_clothes_rail_40_36',
        '20313284_shelf_clothes_rail_80_36',
        '90319268_shelf_clothes_rail_40_36',
        '70319269_shelf_clothes_rail_80_36',
      ];

      const insertsRequiring510 = [
        '60296174', // Shelf, white, 40x51
        '10296176', // Shelf, white, 80x51
        '20296289', // Shelf, bamboo, 40x51
        '80296291', // Shelf, bamboo, 80x51
        'shelf_drawer_40_51_white',
        'shelf_drawer_80_51_white',
        'shelf_drawer_40_51_bamboo',
        'shelf_drawer_80_51_bamboo',
        '60296174_shelf_clothes_rail_40_51',
        '10296176_shelf_clothes_rail_80_51',
        '20296289_shelf_clothes_rail_40_51',
        '80296291_shelf_clothes_rail_80_51',
      ];

      const insertsFittingBoth = [
        '10317292', // Shoe shelf, white, 40x36
        '50317290', // Shoe shelf, white, 80x36
        '40296212', // Clothes rail, white, 40
        '00296214', // Clothes rail, white, 80
      ];

      /* Only need to consider left brackets,
         since IOWS doesn't distinguish between left/right brackets. */
      const bracket360 = '00317527_L';
      const bracket510 = '00296172_L';

      const sectionsPosts360 = getSectionsPostsOfDepth(360);
      const sectionsPosts510 = getSectionsPostsOfDepth(510);
      const foundAnInsertRequiring360 =
        !!filterOnPresentAndValid(insertsRequiring360).length;
      const foundAnInsertRequiring510 =
        !!filterOnPresentAndValid(insertsRequiring510).length;
      const foundAnInsertFittingBoth =
        !!filterOnPresentAndValid(insertsFittingBoth).length;
      const foundBracket360 = !!filterOnPresentAndValid([bracket360]).length;
      const foundBracket510 = !!filterOnPresentAndValid([bracket510]).length;

      let wantToInvalidateSectionsPosts360 =
        !foundAnInsertRequiring360 || !foundBracket360;
      const wantToInvalidateSectionsPosts510 =
        !foundAnInsertRequiring510 || !foundBracket510;

      /* Corner case: If sections of both depths are to be invalidated and we have
         an insert without depth preference, we wouldn't have any post sections left
         where this insert can be placed, so we keep the 360 sections after all. */
      if (
        wantToInvalidateSectionsPosts360 &&
        wantToInvalidateSectionsPosts510 &&
        foundAnInsertFittingBoth
      )
        wantToInvalidateSectionsPosts360 = false;

      wantToInvalidateSectionsPosts360 && invalidateProducts(sectionsPosts360);
      wantToInvalidateSectionsPosts510 && invalidateProducts(sectionsPosts510);
      break;
    default:
      break;
  }
}

export function getMissingMandatoryProductCategories() {
  return productService.getMissingMandatoryProductCategories();
}

export function __test__flagAllAsValid() {
  products.forEach(function validate(product) {
    product.valid = true;
  });
}

export default products;
